import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTeam } from '../contexts/TeamContext';
import TeamSelector from './TeamSelector';

interface LayoutProps {
	children: React.ReactNode;
	title: string;
	subtitle?: string;
}

export default function Layout({ children, title, subtitle }: LayoutProps) {
	const { user, logout } = useAuth();
	const { theme, toggleTheme } = useTheme();
	const { currentTeam } = useTeam();
	const [sidebarOpen, setSidebarOpen] = useState(true);
	const location = useLocation();

	const isMember = currentTeam?.role === 'member';

	const navigation = isMember
		? [
			{ name: 'Guide', href: '/getting-started', icon: 'guide' },
			{ name: 'Chat', href: '/chat', icon: 'chat' },
			{ name: 'Profile', href: '/profile', icon: 'settings' },
		]
		: [
			{ name: 'Guide', href: '/getting-started', icon: 'guide' },
			{ name: 'Overview', href: '/dashboard', icon: 'home' },
			{ name: 'Devices', href: '/machines', icon: 'server' },
			{ name: 'Chat', href: '/chat', icon: 'chat' },
			{ name: 'Connections', href: '/connections', icon: 'plug' },
			{ name: 'Events', href: '/events', icon: 'activity' },
			{ name: 'Team', href: '/team', icon: 'users' },
			{ name: 'Downloads', href: '/downloads', icon: 'download' },
			{ name: 'Settings', href: '/settings', icon: 'settings' },
		];

	const isCurrentPath = (href: string) => {
		if (href === '/machines') {
			return location.pathname === '/machines' || location.pathname.startsWith('/machines/');
		}
		if (href === '/team') {
			return location.pathname === '/team' || location.pathname.startsWith('/team/');
		}
		return location.pathname === href;
	};

	const NavIcon = ({ icon }: { icon: string }) => {
		switch (icon) {
			case 'home':
				return (
					<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
					</svg>
				);
			case 'server':
				return (
					<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
					</svg>
				);
			case 'activity':
				return (
					<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
					</svg>
				);
			case 'chat':
				return (
					<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
					</svg>
				);
			case 'users':
				return (
					<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
					</svg>
				);
			case 'settings':
				return (
					<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
					</svg>
				);
			case 'guide':
				return (
					<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
					</svg>
				);
			case 'download':
				return (
					<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
					</svg>
				);
			case 'plug':
				return (
					<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
					</svg>
				);
			default:
				return null;
		}
	};

	return (
		<div className="min-h-screen bg-white dark:bg-slate-950">
			<div className="flex">
				{/* Sidebar */}
				<div className={`${sidebarOpen ? 'w-64' : 'w-20'} transition-all duration-300 ease-in-out bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 h-screen sticky top-0 flex flex-col`}>
					{/* Logo */}
					<div className="flex items-center justify-between h-16 px-4 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
						{sidebarOpen && (
							<Link to="/dashboard" className="flex items-center space-x-2">
								<img src="/img/logo.png" alt="ClawdGate" className="w-8 h-8 rounded-lg shadow-lg" />
								<span className="font-heading text-xl font-bold text-red-600 dark:text-red-500">ClawdGate</span>
							</Link>
						)}
						<button
							onClick={() => setSidebarOpen(!sidebarOpen)}
							className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
						>
							<svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								{sidebarOpen ? (
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
								) : (
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
								)}
							</svg>
						</button>
					</div>

					{/* Navigation */}
					<nav className="flex-1 mt-6 px-3 space-y-1 overflow-y-auto">
						{navigation.map((item) => {
							const current = isCurrentPath(item.href);
							return (
								<Link
									key={item.name}
									to={item.href}
									className={`flex items-center ${sidebarOpen ? 'px-4' : 'px-3 justify-center'} py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer group ${
										current
											? 'bg-gradient-to-r from-primary-500 to-secondary-600 text-white shadow-lg'
											: 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
									}`}
								>
									<div className={current ? 'text-white' : 'text-slate-600 dark:text-slate-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors'}>
										<NavIcon icon={item.icon} />
									</div>
									{sidebarOpen && <span className="ml-3">{item.name}</span>}
								</Link>
							);
						})}
					</nav>

					{/* Bottom section: Mobile App + Powered by OpenClaw + Theme Toggle + User Profile */}
					<div className="mt-auto flex-shrink-0">
						{/* Mobile App Links */}
						{sidebarOpen ? (
							<div className="px-4 pb-2">
								<div className="px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
									<p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5">Mobile App</p>
									<div className="flex items-center gap-2">
										<a href="https://apps.apple.com/us/app/clawdgate/id6758634972" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors" title="App Store">
											<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
											iOS
										</a>
										<a href="https://download.clawdgate.com/mobile/android/clawdgate-latest.apk" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors" title="Download APK">
											<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.523 15.341a.96.96 0 0 0-.953.953c0 .533.42.954.953.954a.96.96 0 0 0 .954-.954.96.96 0 0 0-.954-.953zm-11.046 0a.96.96 0 0 0-.954.953c0 .533.42.954.954.954a.96.96 0 0 0 .953-.954.96.96 0 0 0-.953-.953zm11.4-6.127l1.998-3.464a.416.416 0 0 0-.152-.567.416.416 0 0 0-.568.152l-2.024 3.51A12.17 12.17 0 0 0 12 7.876c-1.82 0-3.542.39-5.131.969L4.845 5.335a.416.416 0 0 0-.568-.152.416.416 0 0 0-.152.567L6.123 9.214C2.702 11.01.498 14.173.498 17.83h23.004c0-3.657-2.204-6.82-5.625-8.616z" /></svg>
											Android
										</a>
									</div>
								</div>
							</div>
						) : (
							<div className="flex justify-center gap-1 pb-2">
								<a href="https://apps.apple.com/us/app/clawdgate/id6758634972" target="_blank" rel="noopener noreferrer" title="iOS App" className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
									<svg className="w-4 h-4 text-slate-500 dark:text-slate-400" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
								</a>
								<a href="https://download.clawdgate.com/mobile/android/clawdgate-latest.apk" target="_blank" rel="noopener noreferrer" title="Android APK" className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
									<svg className="w-4 h-4 text-slate-500 dark:text-slate-400" viewBox="0 0 24 24" fill="currentColor"><path d="M17.523 15.341a.96.96 0 0 0-.953.953c0 .533.42.954.953.954a.96.96 0 0 0 .954-.954.96.96 0 0 0-.954-.953zm-11.046 0a.96.96 0 0 0-.954.953c0 .533.42.954.954.954a.96.96 0 0 0 .953-.954.96.96 0 0 0-.953-.953zm11.4-6.127l1.998-3.464a.416.416 0 0 0-.152-.567.416.416 0 0 0-.568.152l-2.024 3.51A12.17 12.17 0 0 0 12 7.876c-1.82 0-3.542.39-5.131.969L4.845 5.335a.416.416 0 0 0-.568-.152.416.416 0 0 0-.152.567L6.123 9.214C2.702 11.01.498 14.173.498 17.83h23.004c0-3.657-2.204-6.82-5.625-8.616z" /></svg>
								</a>
							</div>
						)}

						{/* Powered by OpenClaw */}
						{sidebarOpen ? (
							<div className="px-4 pb-2">
								<a href="https://github.com/clawdbot/openclaw" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
									<img src="/img/openclaw-logo.png" alt="OpenClaw" className="w-5 h-5 rounded" />
									<span className="text-xs text-slate-500 dark:text-slate-400">Powered by <span className="font-semibold text-slate-700 dark:text-slate-300">OpenClaw</span></span>
								</a>
							</div>
						) : (
							<div className="flex justify-center pb-2">
								<a href="https://github.com/clawdbot/openclaw" target="_blank" rel="noopener noreferrer" title="Powered by OpenClaw">
									<img src="/img/openclaw-logo.png" alt="OpenClaw" className="w-7 h-7 rounded" />
								</a>
							</div>
						)}
						{/* Theme Toggle */}
						<div className="px-3">
							<button
								onClick={toggleTheme}
								className={`flex items-center ${sidebarOpen ? 'w-full px-4 justify-start' : 'w-full px-3 justify-center'} py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800`}
							>
								{theme === 'light' ? (
									<>
										<svg className="w-6 h-6 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
										</svg>
										{sidebarOpen && <span className="ml-3">Dark Mode</span>}
									</>
								) : (
									<>
										<svg className="w-6 h-6 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
										</svg>
										{sidebarOpen && <span className="ml-3">Light Mode</span>}
									</>
								)}
							</button>
						</div>

						{/* User Profile */}
						<div className="p-4 border-t border-slate-200 dark:border-slate-800">
							{sidebarOpen ? (
								<div className="flex items-center space-x-3">
									<Link
										to="/profile"
										className={`flex items-center space-x-3 flex-1 min-w-0 p-1 -m-1 rounded-lg transition-colors ${
											location.pathname === '/profile'
												? 'bg-slate-100 dark:bg-slate-800'
												: 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
										}`}
									>
										{user?.photoURL ? (
											<img src={user.photoURL} alt="" className="w-10 h-10 rounded-full shadow-md flex-shrink-0 object-cover" referrerPolicy="no-referrer" />
										) : (
											<div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-full flex items-center justify-center text-white font-semibold shadow-md flex-shrink-0">
												{(user?.displayName || user?.email || '?').charAt(0).toUpperCase()}
											</div>
										)}
										<div className="flex-1 min-w-0">
											<p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{user?.displayName || user?.email}</p>
											<p className="text-xs text-slate-500 dark:text-slate-400">{currentTeam?.license?.type === 'active' ? 'Pro' : currentTeam?.license?.type === 'expired' ? 'Expired' : 'Free Trial'}</p>
										</div>
									</Link>
									<button
										onClick={logout}
										className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer flex-shrink-0"
										title="Sign out"
									>
										<svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
										</svg>
									</button>
								</div>
							) : (
								<div className="flex flex-col items-center space-y-2">
									<Link
										to="/profile"
										className={`p-1 rounded-lg transition-colors ${
											location.pathname === '/profile'
												? 'bg-slate-100 dark:bg-slate-800'
												: 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
										}`}
										title="Profile"
									>
										{user?.photoURL ? (
											<img src={user.photoURL} alt="" className="w-10 h-10 rounded-full shadow-md object-cover" referrerPolicy="no-referrer" />
										) : (
											<div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-full flex items-center justify-center text-white font-semibold shadow-md">
												{(user?.displayName || user?.email || '?').charAt(0).toUpperCase()}
											</div>
										)}
									</Link>
									<button
										onClick={logout}
										className="w-full flex justify-center p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
										title="Sign out"
									>
										<svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
										</svg>
									</button>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Main Content */}
				<div className="flex-1 flex flex-col min-h-screen bg-slate-50 dark:bg-slate-950">
					<div className="flex-1 p-8">
						{/* Header */}
						<div className="mb-8">
							<div className="flex items-center justify-between mb-4">
								<h1 className="font-heading text-4xl font-bold text-slate-900 dark:text-white">{title}</h1>
								<TeamSelector />
							</div>
							{subtitle && <p className="text-slate-600 dark:text-slate-400">{subtitle}</p>}
						</div>

						{children}
					</div>

					{/* Footer */}
					<footer className="px-8 py-4 border-t border-slate-200 dark:border-slate-800">
						<div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-500">
							<div className="flex items-center gap-2">
								<span>&copy; {new Date().getFullYear()} SignifyID. All rights reserved.</span>
								<span className="text-slate-300 dark:text-slate-700">|</span>
								<a href="https://github.com/clawdbot/openclaw" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
									<img src="/img/openclaw-logo.png" alt="" className="w-3.5 h-3.5 rounded-sm" />
									Powered by OpenClaw
								</a>
							</div>
							<div className="flex items-center gap-4">
								<a href="https://apps.apple.com/us/app/clawdgate/id6758634972" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300 transition-colors" title="Download on the App Store">
									<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
									iOS
								</a>
								<a href="https://download.clawdgate.com/mobile/android/clawdgate-latest.apk" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300 transition-colors" title="Download Android APK">
									<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.523 15.341a.96.96 0 0 0-.953.953c0 .533.42.954.953.954a.96.96 0 0 0 .954-.954.96.96 0 0 0-.954-.953zm-11.046 0a.96.96 0 0 0-.954.953c0 .533.42.954.954.954a.96.96 0 0 0 .953-.954.96.96 0 0 0-.953-.953zm11.4-6.127l1.998-3.464a.416.416 0 0 0-.152-.567.416.416 0 0 0-.568.152l-2.024 3.51A12.17 12.17 0 0 0 12 7.876c-1.82 0-3.542.39-5.131.969L4.845 5.335a.416.416 0 0 0-.568-.152.416.416 0 0 0-.152.567L6.123 9.214C2.702 11.01.498 14.173.498 17.83h23.004c0-3.657-2.204-6.82-5.625-8.616z" /></svg>
									Android
								</a>
								<span className="text-slate-300 dark:text-slate-700">|</span>
								<a href="https://clawdgate.com" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors">Website</a>
								<a href="https://clawdgate.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors">Privacy</a>
								<a href="https://clawdgate.com/terms" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors">Terms</a>
							</div>
						</div>
					</footer>
				</div>
			</div>
		</div>
	);
}
